import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import client from "../../../api/camasirhaneApi";
import { MONTHS, YEARS, formatNum, formatInteger } from "../utils/constants";
import { downloadDashboardExcelReport } from "../utils/dashboardExcelReport";
import { downloadDashboardPdfReport } from "../utils/dashboardPdfReport";
import "../styles/shared.css";
import "../styles/dashboard.css";

const C = {
  dirty: "#2C3E50",
  clean: "#3498DB",
  prod: "#F39C12",
  danger: "#C0392B",
  success: "#27AE60",
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="dash-tooltip">
      <div className="dash-tooltip-title">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="dash-tooltip-row">
          <span className="dot" style={{ background: p.color }} />
          <span className="lbl">{p.name}</span>
          <span className="val">{formatNum(p.value, 1)} kg</span>
        </div>
      ))}
    </div>
  );
}

export default function DashboardScreen() {
  const [year, setYear] = useState(2026);
  const [months, setMonths] = useState([6]);
  const [periodData, setPeriodData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const monthsKey = months.join(",");

  useEffect(() => {
    const selected = monthsKey ? monthsKey.split(",").map(Number) : [];
    if (!selected.length) { setPeriodData([]); return; }
    setLoading(true);
    Promise.all(selected.map((m) =>
      client.get("/reports/period-summary/", { params: { year, month: m } })
        .then((res) => ({ month: m, data: res.data }))
        .catch(() => ({ month: m, data: null }))
    )).then((results) => {
      setPeriodData(results.sort((a, b) => a.month - b.month));
    }).finally(() => setLoading(false));
  }, [year, monthsKey]);

  function toggleMonth(m) {
    setMonths((prev) => prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m].sort((a, b) => a - b));
  }

  // Seçilen dönemleri birleştir
  const agg = useMemo(() => {
    const grand = { dirty_kg: 0, clean_kg: 0, production_kg: 0 };
    const hotelMap = new Map();
    const deptMap = new Map();
    const trend = [];
    periodData.forEach(({ month, data }) => {
      const g = data?.grand_totals || {};
      const gd = Number(g.dirty_kg) || 0, gc = Number(g.clean_kg) || 0, gp = Number(g.production_kg) || 0;
      grand.dirty_kg += gd; grand.clean_kg += gc; grand.production_kg += gp;
      trend.push({ name: MONTHS[month - 1].slice(0, 3), Kirli: gd, Temiz: gc, "Üretim": gp });
      (data?.hotels || []).forEach((h) => {
        const cur = hotelMap.get(h.hotel_name) || {
          name: h.hotel_name,
          Kirli: 0,
          Temiz: 0,
          "Üretim": 0,
          departments: new Map(),
        };
        cur.Kirli += Number(h.dirty_kg) || 0;
        cur.Temiz += Number(h.clean_kg) || 0;
        cur["Üretim"] += Number(h.production_kg) || 0;
        (h.departments || []).forEach((d) => {
          const dc = cur.departments.get(d.department_name) || { name: d.department_name, Kirli: 0, Temiz: 0 };
          dc.Kirli += Number(d.dirty_kg) || 0;
          dc.Temiz += Number(d.clean_kg) || 0;
          cur.departments.set(d.department_name, dc);

          const globalDc = deptMap.get(d.department_name) || { name: d.department_name, Kirli: 0, Temiz: 0 };
          globalDc.Kirli += Number(d.dirty_kg) || 0;
          globalDc.Temiz += Number(d.clean_kg) || 0;
          deptMap.set(d.department_name, globalDc);
        });
        hotelMap.set(h.hotel_name, cur);
      });
    });
    grand.dirty_clean_diff_kg = grand.dirty_kg - grand.clean_kg;
    grand.clean_production_diff_kg = grand.clean_kg - grand.production_kg;

    const hotels = [...hotelMap.values()].map((h) => {
      const dirty = h.Kirli;
      const clean = h.Temiz;
      const production = h["Üretim"];
      return {
        name: h.name,
        Kirli: dirty,
        Temiz: clean,
        "Üretim": production,
        dirty_clean_diff_kg: dirty - clean,
        clean_production_diff_kg: clean - production,
        efficiency: clean ? (production / clean) * 100 : 0,
        departments: [...h.departments.values()]
          .map((d) => ({ ...d, Fark: d.Kirli - d.Temiz }))
          .sort((a, b) => (b.Kirli + b.Temiz) - (a.Kirli + a.Temiz)),
      };
    }).sort((a, b) => (b.Kirli + b.Temiz) - (a.Kirli + a.Temiz));

    return {
      grand,
      hotelChart: hotels,
      hotels,
      deptChart: [...deptMap.values()]
        .map((d) => ({ ...d, Fark: d.Kirli - d.Temiz }))
        .sort((a, b) => (b.Kirli + b.Temiz) - (a.Kirli + a.Temiz)),
      trend,
    };
  }, [periodData]);

  const grand = agg.grand;
  const efficiency = grand.clean_kg ? (grand.production_kg / grand.clean_kg) * 100 : 0;
  const hasData = grand.dirty_kg || grand.clean_kg || grand.production_kg;
  const multi = months.length > 1;

  const periodLabel = months.length === 0
    ? "—"
    : months.length === 12
      ? `Tüm yıl · ${year}`
      : `${months.map((m) => MONTHS[m - 1]).join(", ")} · ${year}`;

  async function handleExport() {
    if (!months.length) return;
    setExporting(true);
    try {
      await downloadDashboardExcelReport({
        periodLabel,
        year,
        months,
        grand,
        hotels: agg.hotels,
        deptChart: agg.deptChart,
      });
    } finally {
      setExporting(false);
    }
  }

  function handlePdfExport() {
    if (!hasData) return;
    downloadDashboardPdfReport({
      periodLabel,
      year,
      months,
      grand,
      efficiency,
      hotelChart: agg.hotelChart,
      hotels: agg.hotels,
      deptChart: agg.deptChart,
    });
  }

  const kpis = [
    { key: "dirty", label: "Toplam Kirli", value: grand.dirty_kg, unit: "kg", accent: C.dirty },
    { key: "clean", label: "Toplam Temiz", value: grand.clean_kg, unit: "kg", accent: C.clean },
    { key: "prod", label: "Toplam Üretim", value: grand.production_kg, unit: "kg", accent: C.prod },
    { key: "eff", label: "Üretim / Temiz Verimi", value: efficiency, unit: "%", accent: C.success, digits: 1 },
    { key: "d1", label: "Kirli − Temiz Fark", value: grand.dirty_clean_diff_kg, unit: "kg", accent: C.dirty, diff: true },
    { key: "d2", label: "Temiz − Üretim Fark", value: grand.clean_production_diff_kg, unit: "kg", accent: C.prod, diff: true },
  ];

  return (
    <div className="screen">
      <div className="screen-header">
        <h2>Çamaşırhane Panosu</h2>
        <p>{periodLabel} — kirli, temiz ve üretim özetleri tek bakışta</p>
      </div>

      <div className="toolbar dash-toolbar">
        <select className="ui-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="month-chips">
          {MONTHS.map((name, i) => {
            const m = i + 1;
            const on = months.includes(m);
            return (
              <button key={name} type="button" className={`month-chip${on ? " active" : ""}`} onClick={() => toggleMonth(m)}>
                {name.slice(0, 3)}
              </button>
            );
          })}
        </div>
        <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => setMonths([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])}>Tüm yıl</button>
        <button className="ui-btn ui-btn--sm ui-btn--ghost" onClick={() => setMonths([])}>Temizle</button>
        <button
          className="ui-btn ui-btn--sm"
          onClick={handleExport}
          disabled={!months.length || exporting}
          title="Pano ozetleri + otel/malzeme bazinda toplam Tartim/Üretim"
        >
          {exporting ? "Hazırlanıyor..." : "Excel Raporu"}
        </button>
        <button
          className="ui-btn ui-btn--sm ui-btn--ghost"
          onClick={handlePdfExport}
          disabled={!hasData || loading}
          title="Secilen donemin ozet sayilari ve grafiklerini PDF olarak indir"
        >
          PDF Raporu
        </button>
        {loading && <span className="status-msg">Yükleniyor...</span>}
      </div>

      {!loading && !months.length && (
        <div className="dash-empty">En az bir ay seçin.</div>
      )}
      {!loading && months.length > 0 && !hasData && (
        <div className="dash-empty">Seçilen dönem(ler) için veri bulunamadı. Yıl/ay seçimini kontrol edin veya Excel Aktarım'dan veri yükleyin.</div>
      )}

      {hasData && (
        <>
          <div className="kpi-grid">
            {kpis.map((k) => {
              const neg = k.diff && Number(k.value) < 0;
              return (
                <div key={k.key} className="kpi-card" style={{ "--accent": k.accent }}>
                  <span className="kpi-label">{k.label}</span>
                  <span className="kpi-value" style={neg ? { color: C.danger } : undefined}>
                    {formatNum(k.value, k.digits ?? (k.unit === "%" ? 1 : 0))}
                    <small>{k.unit}</small>
                  </span>
                </div>
              );
            })}
          </div>

          {multi && (
            <div className="dash-card">
              <h3>Aylık Trend</h3>
              <div className="dash-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={agg.trend} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={3}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#7F8C8D" }} axisLine={{ stroke: "#CAD6DF" }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#7F8C8D" }} axisLine={false} tickLine={false} width={54}
                      tickFormatter={(v) => formatInteger(v)} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(52,152,219,0.08)" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Kirli" fill={C.dirty} radius={[4, 4, 0, 0]} maxBarSize={26} />
                    <Bar dataKey="Temiz" fill={C.clean} radius={[4, 4, 0, 0]} maxBarSize={26} />
                    <Bar dataKey="Üretim" fill={C.prod} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="dash-grid">
            <div className="dash-card">
              <h3>Otel Bazında Karşılaştırma</h3>
              <div className="dash-chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={agg.hotelChart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#7F8C8D" }} axisLine={{ stroke: "#CAD6DF" }} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#7F8C8D" }} axisLine={false} tickLine={false} width={54}
                      tickFormatter={(v) => formatInteger(v)} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(52,152,219,0.08)" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Kirli" fill={C.dirty} radius={[4, 4, 0, 0]} maxBarSize={38} />
                    <Bar dataKey="Temiz" fill={C.clean} radius={[4, 4, 0, 0]} maxBarSize={38} />
                    <Bar dataKey="Üretim" fill={C.prod} radius={[4, 4, 0, 0]} maxBarSize={38} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="dash-card">
              <h3>Departman Bazında Kirli / Temiz</h3>
              <div className="dash-chart">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={agg.deptChart} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#7F8C8D" }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => formatInteger(v)} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "#7F8C8D" }}
                      axisLine={{ stroke: "#CAD6DF" }} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(52,152,219,0.08)" }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Kirli" fill={C.dirty} radius={[0, 4, 4, 0]} maxBarSize={16} />
                    <Bar dataKey="Temiz" fill={C.clean} radius={[0, 4, 4, 0]} maxBarSize={16} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="dash-card">
            <h3>Departman Özeti</h3>
            <div className="grid-wrapper" style={{ boxShadow: "none", border: "none" }}>
              <table className="data-grid">
                <thead>
                  <tr>
                    <th className="sticky-col label-col">Departman</th>
                    <th>Kirli (kg)</th>
                    <th>Temiz (kg)</th>
                    <th>Kirli − Temiz</th>
                    <th>Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {agg.deptChart.map((d) => {
                    const share = grand.dirty_kg ? (d.Kirli / grand.dirty_kg) * 100 : 0;
                    return (
                      <tr key={d.name} className="data-row">
                        <td className="sticky-col label-col mat-cell">{d.name}</td>
                        <td className="num">{formatNum(d.Kirli, 1)}</td>
                        <td className="num">{formatNum(d.Temiz, 1)}</td>
                        <td className="num" style={d.Fark ? { color: C.danger, fontWeight: 700 } : undefined}>{formatNum(d.Fark, 1)}</td>
                        <td className="share-cell">
                          <span className="share-bar"><span style={{ width: `${Math.min(100, share)}%` }} /></span>
                          <span className="share-num">%{formatNum(share, 1)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="grand-row">
                    <td className="sticky-col label-col grand-label">Genel Toplam</td>
                    <td className="num">{formatNum(grand.dirty_kg, 1)}</td>
                    <td className="num">{formatNum(grand.clean_kg, 1)}</td>
                    <td className="num grand-total">{formatNum(grand.dirty_clean_diff_kg, 1)}</td>
                    <td className="num">%100</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
